import { Mode } from "@/api/types/mode.enum";
import { Steps } from "@/api/types/steps.enum";
import { defineStore } from "pinia";
import { ref } from "vue";

export const useBookingStepStore = defineStore("bookingStep", () => {
  const step = ref(Steps.Date);
  const mode = ref(Mode.Normal);
  const bookingId = ref('');
  const checkoutId = ref('');
  const allowMenu2 = ref(false);
  const allowMenu = ref(false);
  // Set when a coupon fully covers the total (remainingDueCents === 0) so the
  // confirmation step can skip Stripe and render the booking as already paid.
  // Session-only — never persisted, it only lives for the confirmation render.
  const freeBooking = ref(false);
  console.log('mode init', mode.value, step.value);
  const steps: Steps[] = [Steps.Date, Steps.Type, Steps.Menu, Steps.Menu2, Steps.Drinks, Steps.Options, Steps.Info, Steps.Summary, Steps.SummaryMenu, Steps.Confirmation]
  // The lodging upsell step (EAUG-55a) was removed on 2026-10-23. It came right
  // before Options, so a visitor saved on it resumes there; its flag goes.
  localStorage.removeItem("booking_offer_lodging");
  if (localStorage.getItem("booking_step") === "Lodging") {
    localStorage.setItem("booking_step", Steps.Options);
  }
  const key = localStorage.getItem("booking_step");
  const key2 = localStorage.getItem("booking_mode");
  // Only menu links from before the 2026-10 fix saved their token here — and
  // they also saved the linked booking (someone else's allergy, remarks and
  // email, often from a link the organiser shared) into the cart. Drop it all,
  // step included, before the booking store can restore the cart.
  const preFixMenuLink = localStorage.getItem("booking_id") !== null;
  if (preFixMenuLink) {
    localStorage.removeItem("booking_id");
    localStorage.removeItem("booking_data");
  }
  // The mode is never restored: booking.vue applies it from the URL on every
  // load (menu link, noon invite or normal) — a restored Noon mode would have
  // no invite token, a restored Menu mode the previous link's booking. Nor is
  // a step saved by a menu link: it belongs to that link, not to this
  // visitor's own wizard. Restore without dispatching stepChanged — its
  // listener would read the stores while they are still being built.
  if (preFixMenuLink || key2 === Mode.Menu) {
    localStorage.removeItem("booking_step");
  } else if (key !== null && (Object.values(Steps) as string[]).includes(key)) {
    step.value = key as Steps;
  } else if (key !== null) {
    // A step this build does not know (saved by a newer one, then rolled
    // back): Steps[key] would be undefined and no step would render.
    localStorage.removeItem("booking_step");
  }

  // Whether the wizard stops at step s: no dinner → no Menu/Menu2/Drinks;
  // no dish choice open → no Menu2.
  function applies(s: Steps): boolean {
    switch (s) {
      case Steps.Menu:
      case Steps.Drinks:
        return allowMenu.value;
      case Steps.Menu2:
        return allowMenu.value && allowMenu2.value;
      default:
        return true;
    }
  }

  function reset() {
    step.value = steps[0];
    const event = new Event("stepChanged");
    document.dispatchEvent(event);
  }

  function nextStep() {
    let i = steps.indexOf(step.value) + 1;
    while (i < steps.length - 1 && !applies(steps[i])) i++;
    console.log('next step', i, steps[i]);
    step.value = steps[i];
    localStorage.setItem('booking_step', step.value);
    const event = new Event("stepChanged");
    document.dispatchEvent(event);
  }

  function goToStep(newStep: Steps, force = false) {
    const i = steps.indexOf(step.value);
    const j = steps.indexOf(newStep);
    console.log('goToStep', newStep, i, j);
    if (j < i || force) {
      step.value = steps[j];
      localStorage.setItem('booking_step', step.value);
    }
    console.log('step is set', step.value);
    const event = new Event("stepChanged");
    document.dispatchEvent(event);
  }

  function previousStep() {
    let i = Math.max(steps.indexOf(step.value) - 1, 0);
    while (i > 0 && !applies(steps[i])) i--;
    console.log('new step', i, steps[i]);
    step.value = steps[i];
    localStorage.setItem('booking_step', step.value);
    const event = new Event("stepChanged");
    document.dispatchEvent(event);
  }

  function setMode(m: Mode) {
    console.log('set Mode', m);
    mode.value = m;
    localStorage.setItem('booking_mode', mode.value);
    if (m == Mode.Menu) {
      goToStep(Steps.Menu2, true);
    } else if (m == Mode.Noon) {
      goToStep(Steps.Type, true);   // skip date step — already set from invite
    } else {
      if (step.value == Steps.SummaryMenu) {
        goToStep(Steps.Date);
      }
    }
  }

  function getMode() {
    return mode.value;
  }

  // Session-only: the menu link carries its booking token in the URL on every
  // load, and the next load must not inherit it.
  function setBookingId(id: string) {
    console.log('setBookingIdStep', id);
    bookingId.value = id;
  }

  return { step, bookingId, checkoutId, allowMenu, allowMenu2, freeBooking, nextStep, previousStep, goToStep, reset, setMode, getMode, setBookingId };
})
